import React from 'react';
import { cn } from '@/lib/utils';

/**
 * Tela "Dashboard" (rota `/`) — ver prints/06-dashboard.png.
 * Ordem: DashGreeting → [OperationHero (flex 2) + DashCard Batalhões (flex 1)] → KpiStrip →
 * [DashCard Histórico de operações (flex 2) + DashCard Como conheceram (flex 1)] →
 * [DashCard Últimas inscrições (flex 2) + coluna com Pendências e Camisetas (flex 1)].
 * Linhas: `flex flex-wrap gap-5`; filhos `flex-[2_1_560px]` e `flex-[1_1_320px]`, sempre `min-w-0`.
 * Sem gráfico e sem financeiro — o painel mostra o efetivo, não o caixa.
 */

export const DashRow: React.FC<{
  children: React.ReactNode;
  align?: 'stretch' | 'start';
}> = ({ children, align = 'stretch' }) => (
  <div
    className={cn(
      'flex flex-wrap gap-5',
      align === 'start' ? 'items-start' : 'items-stretch',
    )}
  >
    {children}
  </div>
);

export const DashGreeting: React.FC<{ date: string; name: string }> = ({
  date,
  name,
}) => (
  <div className="flex flex-wrap items-end gap-x-5 gap-y-3">
    <span className="mr-auto flex flex-col gap-1.5">
      <span className="col-label text-faint">{date}</span>
      <span className="font-display text-[clamp(22px,2vw,28px)] font-extrabold leading-[1.1] tracking-[-.025em] text-white">
        Bem-vindo, {name}.
      </span>
    </span>
  </div>
);

/** Card padrão do dashboard: cabeçalho com título Barlow 16px caixa alta + subtítulo + ação à direita. */
export const DashCard: React.FC<{
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  size?: 'wide' | 'narrow' | 'auto';
  bodyClassName?: string;
  children: React.ReactNode;
}> = ({ title, subtitle, action, size = 'auto', bodyClassName, children }) => (
  <section
    className={cn(
      'flex min-w-0 flex-col overflow-hidden rounded-2xl border border-border bg-card',
      size === 'wide' && 'flex-[2_1_560px]',
      size === 'narrow' && 'flex-[1_1_320px]',
    )}
  >
    <header className="flex items-center gap-3 border-b border-border px-6 py-5">
      <span className="mr-auto flex flex-col gap-[5px]">
        <span className="font-display text-base font-extrabold uppercase tracking-[-.02em] text-white">
          {title}
        </span>
        {subtitle && (
          <span className="text-[12.5px] text-muted-foreground">{subtitle}</span>
        )}
      </span>
      {action}
    </header>
    <div className={cn('flex flex-1 flex-col', bodyClassName)}>{children}</div>
  </section>
);

/** Link "Ver todos ›" em areia no cabeçalho de card. */
export const DashLink: React.FC<{
  onClick?: () => void;
  children: React.ReactNode;
  icon: React.ReactNode;
}> = ({ onClick, children, icon }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex items-center gap-1.5 text-xs font-bold text-sand hover:opacity-85"
  >
    {children}
    {icon}
  </button>
);

/** Card hero da próxima operação — foto do site sob gradiente horizontal, contagem regressiva em areia. */
export const OperationHero: React.FC<{
  name: string;
  dateLabel: string;
  city: string;
  daysLeft: number;
  /** `false` quando não há operação futura e o hero mostra a última realizada. */
  open?: boolean;
  enrolled: number;
  paid: number;
  coverUrl: string;
  calendarIcon: React.ReactNode;
  pinIcon: React.ReactNode;
  actions: React.ReactNode;
}> = ({
  name,
  dateLabel,
  city,
  daysLeft,
  open = true,
  enrolled,
  paid,
  coverUrl,
  calendarIcon,
  pinIcon,
  actions,
}) => {
  const pending = enrolled - paid;
  const pct = enrolled ? Math.round((paid / enrolled) * 100) : 0;
  return (
    <section
      className="relative flex min-h-[300px] min-w-0 flex-[2_1_560px] overflow-hidden rounded-[18px] border border-border bg-cover bg-[center_40%]"
      style={{ backgroundImage: `url(${coverUrl})` }}
    >
      <span className="absolute inset-0 bg-[linear-gradient(90deg,color-mix(in_oklch,var(--sunken)_96%,transparent)_0%,color-mix(in_oklch,var(--sunken)_86%,transparent)_48%,color-mix(in_oklch,var(--sunken)_55%,transparent)_100%)]" />
      <div className="relative flex flex-1 flex-col gap-[22px] px-[clamp(22px,2.4vw,34px)] py-7">
        <span className="flex flex-wrap items-center gap-2.5">
          <span className="col-label flex items-center gap-2 tracking-[.18em] text-sand-dim">
            <span className="h-px w-[18px] bg-sand-dim" />
            {open ? 'Próxima operação' : 'Última operação'}
          </span>
          <StatusPill tone={open ? 'olive' : 'muted'}>
            {open ? 'Inscrições abertas' : 'Concluída'}
          </StatusPill>
        </span>

        <div className="flex flex-wrap items-end gap-x-9 gap-y-5">
          <span className="flex min-w-0 flex-[1_1_280px] flex-col gap-3">
            <span className="font-display text-[clamp(32px,3.4vw,48px)] font-extrabold uppercase leading-[.95] tracking-[-.035em] text-white">
              {name}
            </span>
            <span className="flex flex-wrap gap-x-[18px] gap-y-2 text-[13.5px] text-muted-foreground">
              <span className="flex items-center gap-[7px]">
                <span className="text-olive-icon">{calendarIcon}</span>
                {dateLabel}
              </span>
              <span className="flex items-center gap-[7px]">
                <span className="text-olive-icon">{pinIcon}</span>
                {city}
              </span>
            </span>
          </span>
          <span className="flex shrink-0 items-end gap-2.5">
            <span className="font-display text-[clamp(56px,5.4vw,84px)] font-extrabold leading-[.8] tracking-[-.05em] text-sand">
              {daysLeft}
            </span>
            <span className="col-label flex flex-col gap-[3px] pb-1 text-muted-foreground">
              <span>Dias para</span>
              <span>a partida</span>
            </span>
          </span>
        </div>

        <div className="mt-auto flex flex-col gap-3">
          <span className="flex flex-wrap items-baseline gap-x-[18px] gap-y-1.5 text-[13px] text-muted-foreground">
            <span className="col-label">Efetivo</span>
            <span>
              <strong className="font-display font-extrabold text-white">
                {enrolled}
              </strong>{' '}
              inscritos
            </span>
            <span>
              <strong className="font-display font-extrabold text-olive-ink">
                {paid}
              </strong>{' '}
              pagos
            </span>
            <span>
              <strong className="font-display font-extrabold text-sand">{pending}</strong>{' '}
              aguardando pagamento
            </span>
          </span>
          <span className="flex h-2 gap-0.5 overflow-hidden rounded-full bg-sand/10">
            <span className="bg-olive-icon" style={{ width: `${pct}%` }} />
            <span className="bg-sand-dim" style={{ width: `${100 - pct}%` }} />
          </span>
          <span className="flex flex-wrap gap-2.5 pt-1.5">{actions}</span>
        </div>
      </div>
    </section>
  );
};

/** Faixa única com 4 KPIs separados por hairline vertical (não são 4 cards soltos). */
export const KpiStrip: React.FC<{
  items: {
    label: string;
    value: string;
    note: string;
    noteTone?: 'olive' | 'sand' | 'muted';
    icon: React.ReactNode;
    iconTone?: 'olive' | 'sand' | 'danger';
  }[];
}> = ({ items }) => (
  <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,230px),1fr))] overflow-hidden rounded-2xl border border-border bg-card">
    {items.map((k) => (
      <div
        key={k.label}
        className="-ml-px flex flex-col gap-3.5 border-l border-border px-6 py-[22px]"
      >
        <span className="flex items-center gap-2.5">
          <span
            className={cn(
              'flex size-[30px] shrink-0 items-center justify-center rounded-[9px] border border-border',
              k.iconTone === 'sand'
                ? 'text-sand'
                : k.iconTone === 'danger'
                  ? 'text-danger-soft'
                  : 'text-olive-icon',
            )}
          >
            {k.icon}
          </span>
          <span className="col-label text-muted-foreground">{k.label}</span>
        </span>
        <span className="flex items-baseline gap-2.5">
          <span className="font-display text-[34px] font-extrabold leading-none tracking-[-.035em] text-white">
            {k.value}
          </span>
          <span
            className={cn(
              'text-xs font-semibold',
              k.noteTone === 'olive'
                ? 'text-olive-ink'
                : k.noteTone === 'sand'
                  ? 'text-sand'
                  : 'text-muted-foreground',
            )}
          >
            {k.note}
          </span>
        </span>
      </div>
    ))}
  </div>
);

/** Pílula de status 24px, caixa alta, com ponto. Sempre `whitespace-nowrap`. */
export const StatusPill: React.FC<{
  tone: 'olive' | 'sand' | 'muted';
  children: React.ReactNode;
}> = ({ tone, children }) => (
  <span
    className={cn(
      'col-label flex h-6 items-center gap-[7px] whitespace-nowrap rounded-full border px-2.5 tracking-[.12em]',
      tone === 'olive' && 'border-olive-line/40 bg-accent text-olive-ink',
      tone === 'sand' && 'border-sand/35 text-sand',
      tone === 'muted' && 'border-border text-muted-foreground',
    )}
  >
    <span className="size-1.5 rounded-full bg-current" />
    {children}
  </span>
);

/** Linha do "Histórico de operações": bloco de data 52px, nome/cidade, nº de recrutas, status. */
export const OperationRow: React.FC<{
  day: string;
  month: string;
  year: string;
  name: string;
  city: string;
  soldiers: number;
  open?: boolean;
  pinIcon: React.ReactNode;
  onClick?: () => void;
}> = ({ day, month, year, name, city, soldiers, open, pinIcon, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex w-full items-center gap-[18px] border-b border-border px-6 py-3.5 text-left transition-colors hover:bg-raised"
  >
    <span
      className={cn(
        'flex w-[52px] shrink-0 flex-col items-center gap-[3px] rounded-[11px] border py-[7px]',
        open ? 'border-olive-line bg-accent' : 'border-border bg-sunken',
      )}
    >
      <span
        className={cn(
          'font-display text-lg font-extrabold leading-none tracking-[-.02em]',
          open ? 'text-sand' : 'text-white',
        )}
      >
        {day}
      </span>
      <span className="col-label text-[9.5px] text-faint">{month}</span>
    </span>
    <span className="flex min-w-0 flex-1 flex-col gap-1">
      <span className="truncate text-sm font-semibold text-white">{name}</span>
      <span className="flex items-center gap-1.5 truncate text-xs text-muted-foreground">
        <span className="text-faint">{pinIcon}</span>
        {city} • {year}
      </span>
    </span>
    <span className="flex min-w-[70px] shrink-0 flex-col items-end gap-[3px]">
      <span className="font-display text-lg font-extrabold leading-none tracking-[-.02em] text-white">
        {soldiers}
      </span>
      <span className="col-label text-[9.5px] text-faint">Recrutas</span>
    </span>
    <span className="flex min-w-[118px] shrink-0 justify-end">
      <StatusPill tone={open ? 'olive' : 'muted'}>
        {open ? 'Em inscrição' : 'Concluída'}
      </StatusPill>
    </span>
  </button>
);

/** Linha de batalhão: selo 32px com o ordinal, nome, contagem e barra de 4px (100% = maior batalhão). */
export const BattalionRow: React.FC<{
  tag: string;
  name: string;
  count: number;
  max: number;
}> = ({ tag, name, count, max }) => (
  <span className="flex items-center gap-3">
    <span className="font-display flex size-8 shrink-0 items-center justify-center rounded-[9px] border border-border bg-sunken text-[11.5px] font-extrabold text-olive-ink">
      {tag}
    </span>
    <span className="flex min-w-0 flex-1 flex-col gap-[7px]">
      <span className="flex items-baseline gap-2">
        <span className="text-[13.5px] font-semibold text-foreground">{name}</span>
        <span className="font-display ml-auto text-sm font-extrabold text-white">
          {count}
        </span>
      </span>
      <span className="h-1 rounded-full bg-sand/10">
        <span
          className="block h-full rounded-full bg-olive-icon"
          style={{ width: `${max ? (count / max) * 100 : 0}%` }}
        />
      </span>
    </span>
  </span>
);

/** Aviso tracejado em areia (tracejado = pendência, não dado). */
export const DashedAlert: React.FC<{
  icon: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ icon, action, children }) => (
  <div className="mx-6 mb-[22px] flex items-center gap-3 rounded-xl border border-dashed border-sand/35 px-3.5 py-3">
    <span className="text-sand">{icon}</span>
    <span className="flex-1 text-[12.5px] text-muted-foreground">{children}</span>
    {action}
  </div>
);

/** Barra horizontal de "Como conheceram" — a primeira (maior) em areia, as demais em oliva. */
export const SourceBar: React.FC<{
  label: string;
  count: number;
  total: number;
  lead?: boolean;
}> = ({ label, count, total, lead }) => {
  const pct = total ? (count / total) * 100 : 0;
  return (
    <span className="flex flex-col gap-[9px]">
      <span className="flex items-baseline gap-2.5">
        <span className="text-[13.5px] font-semibold text-foreground">{label}</span>
        <span className="ml-auto text-xs text-muted-foreground">{count}</span>
        <span
          className={cn(
            'font-display min-w-10 text-right text-sm font-extrabold',
            lead ? 'text-sand' : 'text-white',
          )}
        >
          {Math.round(pct)}%
        </span>
      </span>
      <span className="h-1.5 rounded-full bg-sand/10">
        <span
          className={cn('block h-full rounded-full', lead ? 'bg-sand' : 'bg-olive-icon')}
          style={{ width: `${pct}%` }}
        />
      </span>
    </span>
  );
};

/** Linha de "Últimas inscrições" (grid fixo, min-w 600px dentro de overflow-x-auto). */
export const RecentSoldierRow: React.FC<{
  photo?: string;
  name: string;
  number?: number | null;
  age: number;
  city: string;
  shirtSize: string;
  paid: boolean;
  when: string;
  onClick?: () => void;
}> = ({ photo, name, number, age, city, shirtSize, paid, when, onClick }) => {
  // Foto quebrada (arquivo não migrado) cai nas iniciais em vez do ícone de imagem quebrada.
  const [photoFailed, setPhotoFailed] = React.useState(false);
  return (
    <div
      onClick={onClick}
      className={cn(
        'grid grid-cols-[minmax(170px,1.6fr)_minmax(110px,1fr)_40px_104px_92px] items-center gap-3 border-b border-border px-6 py-3 transition-colors hover:bg-raised',
        onClick && 'cursor-pointer',
      )}
    >
      <span className="flex min-w-0 items-center gap-3">
        {photo && !photoFailed ? (
          <img
            src={photo}
            alt=""
            onError={() => setPhotoFailed(true)}
            className="size-9 shrink-0 rounded-full border border-border object-cover"
          />
        ) : (
          <span className="font-display flex size-9 shrink-0 items-center justify-center rounded-full border border-border bg-accent text-xs font-extrabold text-olive-ink">
            {name.slice(0, 2).toUpperCase()}
          </span>
        )}
        <span className="flex min-w-0 flex-col gap-[3px]">
          <span className="truncate text-sm font-semibold text-white">{name}</span>
          <span className="text-[11.5px] text-faint">
            {number ? `Nº ${number}` : 'Sem nº'} • {age} anos
          </span>
        </span>
      </span>
      <span className="truncate text-[13px] text-muted-foreground">{city}</span>
      <span className="font-display justify-self-start rounded-[7px] border border-border px-2 py-[3px] text-[11px] font-extrabold text-foreground">
        {shirtSize}
      </span>
      <span className="justify-self-start">
        <StatusPill tone={paid ? 'olive' : 'sand'}>
          {paid ? 'Pago' : 'Pendente'}
        </StatusPill>
      </span>
      <span className="text-right text-xs text-muted-foreground">{when}</span>
    </div>
  );
};

/** Item de "Pendências": trilho colorido 4×28, título + dica, contagem Barlow 20px, chevron. */
export const PendingRow: React.FC<{
  label: string;
  hint: string;
  count: number;
  tone: 'sand' | 'danger' | 'faint';
  chevron: React.ReactNode;
  onClick?: () => void;
}> = ({ label, hint, count, tone, chevron, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex w-full items-center gap-3.5 border-b border-border px-6 py-3.5 text-left transition-colors hover:bg-raised"
  >
    <span
      className={cn(
        'h-7 w-1 shrink-0 rounded-full',
        tone === 'sand' ? 'bg-sand' : tone === 'danger' ? 'bg-destructive' : 'bg-faint',
      )}
    />
    <span className="flex min-w-0 flex-1 flex-col gap-[3px]">
      <span className="text-[13.5px] font-semibold text-foreground">{label}</span>
      <span className="text-[11.5px] text-faint">{hint}</span>
    </span>
    <span className="font-display text-xl font-extrabold tracking-[-.02em] text-white">
      {count}
    </span>
    <span className="text-faint">{chevron}</span>
  </button>
);

/** Contador areia 22px ao lado do título do card de Pendências. */
export const CountBadge: React.FC<{ value: number }> = ({ value }) => (
  <span className="font-display flex h-[22px] min-w-[26px] items-center justify-center rounded-full bg-sand px-2 text-[11.5px] font-extrabold text-on-sand">
    {value}
  </span>
);

/** Colunas de tamanho de camiseta (uma coluna por tamanho presente). Maior em areia, demais oliva 55%. Altura 110px. */
export const ShirtSizes: React.FC<{ sizes: { size: string; count: number }[] }> = ({
  sizes,
}) => {
  const max = Math.max(...sizes.map((s) => s.count), 1);
  const total = sizes.reduce((a, s) => a + s.count, 0);
  return (
    <section className="flex flex-col gap-4 rounded-2xl border border-border bg-card px-6 py-5">
      <span className="flex items-baseline gap-2.5">
        <span className="font-display mr-auto text-base font-extrabold uppercase tracking-[-.02em] text-white">
          Camisetas
        </span>
        <span className="text-[11.5px] text-muted-foreground">{total} peças</span>
      </span>
      <div
        className="grid h-[110px] items-end gap-2"
        style={{ gridTemplateColumns: `repeat(${sizes.length}, minmax(0, 1fr))` }}
      >
        {sizes.map((s) => (
          <span
            key={s.size}
            className="flex h-full flex-col items-center justify-end gap-1.5"
          >
            <span className="font-display text-[13px] font-extrabold text-foreground">
              {s.count}
            </span>
            <span
              className={cn(
                'w-full rounded-[6px_6px_2px_2px]',
                s.count === max ? 'bg-sand' : 'bg-olive-icon/55',
              )}
              style={{ height: `${(s.count / max) * 80}%` }}
            />
          </span>
        ))}
      </div>
      <div
        className="-mt-1.5 grid gap-2"
        style={{ gridTemplateColumns: `repeat(${sizes.length}, minmax(0, 1fr))` }}
      >
        {sizes.map((s) => (
          <span
            key={s.size}
            className="text-center text-[10.5px] font-bold tracking-[.1em] text-faint"
          >
            {s.size}
          </span>
        ))}
      </div>
    </section>
  );
};
